import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { saveBlobAsFile } from '../../../api/apiUtils.js'
import {
    deleteUploadEntries,
    downloadMetaListExcel,
    downloadSelctedErrorFile,
    downloadSelctedFile,
    getMetalist,
} from '../../../api/upload.js'
import DeleteIcon from '../../../assets/icons/DeleteIcon.jsx'
import DownloadIcon from '../../../assets/icons/DownloadIcon'
import SearchIconBtn from '../../../assets/icons/SearchIconBtn.jsx'
import { GLOBAL_CONSTANTS } from '../../../constants/globalConstants.js'
import {
    DEFAULT_PAGE,
    getValidCriteria,
    sanitizeForLog,
    sanitizeText,
} from '../../../constants/index.js'
import {
    getColumns,
    getSearchFields,
} from '../../../constants/uploadReturnTab.jsx'
import { useSnackbar } from '../../../contexts/SnackbarProvider'
import { ROLE_CAPABILITIES } from '../../../utils/role-capabilities.js'
import { CommonTable, ConfirmationDialog, Search } from '../../common'
import FileUploadUI from '../Upload/FileUploadSection'

const ReturnUpload = () => {
  const { showSnackbar } = useSnackbar()
  const roleTypes = useSelector(state => state.user.role)
  const roleConfig = ROLE_CAPABILITIES[roleTypes] || ROLE_CAPABILITIES.default

  const [sources, setSources] = useState([])
  const [dataTypes, setDataTypes] = useState([])
  const columns = getColumns(sources, dataTypes)
  const searchFields = getSearchFields(sources, dataTypes)

  const [mockEntities, setMockEntities] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const [startDownloadingSingleFile, setStartDownloadingSingleFile] =
    useState(false)
  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const fetchMetaData = useCallback(
    async (page = 1, pageSize = itemsPerPage, searchList = []) => {
      setLoading(true)
      try {
        const payload = {
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize,
            sortField: 'loadId',
            sortOrder: 'DESC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: searchList,
        }

        const response = await getMetalist(payload)

        setMockEntities(response?.content || [])
        setTotalItems(response?.totalElements || 0)
      } catch (error) {
        console.error('Error fetching meta data:')
      } finally {
        setLoading(false)
      }
    },
    [itemsPerPage]
  )

  useEffect(() => {
    if (roleConfig.canCreate) {
      // initial fetch
      fetchMetaData(currentPage, itemsPerPage, searchCriteria)
      const intervalId = setInterval(() => {
        setCurrentPage(1)
        setItemsPerPage(100)
        fetchMetaData(
          currentPage,
          itemsPerPage,
          getValidCriteria(searchCriteria)
        )
      }, 60000) // 1 minute
      return () => clearInterval(intervalId)
    }
  }, [roleConfig.canCreate])

  // Handle individual item selection
  const handleSelectItem = itemId => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  // Handle select all
  const handleSelectAll = event => {
    if (event.target.checked) {
      setSelectedItems(
        mockEntities
          .filter(item => !(!item?.fileName && (row?.fileSource === 'Reload' || row?.fileSource === 'REPROCESSED_DATA' || row?.fileSource === 'Reprocessed Data')))
          .map(item => item.uuid)
      )
    } else {
      setSelectedItems([])
    }
  }

  // Handle bulk delete
  const confirmDelete = async () => {
    try {
      // Map selected items to API request format
      const payload = mockEntities
        .filter(item => selectedItems.includes(item.uuid))
        .map(item => ({
          uuid: item.uuid,
          templateName: item.fileDataType,
          loadId: item.loadId,
        }))

      await deleteUploadEntries(payload)
      // Update local table state
      fetchMetaData(1, itemsPerPage, [])
      setSelectedItems([])
    } catch (error) {
      showSnackbar(error.message, 'error')
      console.error('Error deleting entries:', error)
    } finally {
      setDeleteDialogOpen(false)
    }
  }

  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return
    setDeleteDialogOpen(true)
  }

  const handleCancel = () => {
    setDeleteDialogOpen(false)
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    fetchMetaData(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  // Handle items per page change
  const handleItemsPerPageChange = perPage => {
    setItemsPerPage(perPage)
    setCurrentPage(1)
    fetchMetaData(1, perPage, getValidCriteria(searchCriteria))
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  // search
  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    // api call
    await fetchMetaData(1, itemsPerPage, searchList)

    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await fetchMetaData(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }

    try {
      setStartDownloading(true)

      const payload = {
        pageFilter: {
          currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'loadId',
          sortOrder: 'DESC',
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
      }

      const blob = await downloadMetaListExcel(payload)
      saveBlobAsFile(blob, 'upload_excel_file.xlsx')
    } catch (error) {
      console.error('Failed to download Excel:', sanitizeForLog(error))
      showSnackbar('Download failed', 'error')
    } finally {
      setStartDownloading(false)
    }
  }
  function getContentType(fileName) {
    const extension = fileName.split('.').pop().toLowerCase()
    switch (extension) {
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      case 'pdf':
        return 'application/pdf'
      default:
        return 'application/octet-stream'
    }
  }

  const handleSingleDownload = async () => {
    if (selectedItems.length === 0) return

    try {
      setStartDownloadingSingleFile(true)

      const payload = mockEntities
        .filter(item => selectedItems.includes(item.uuid))
        .map(item => ({
          uuid: item.uuid,
          loadId: item.loadId,
        }))

      const blob = await downloadSelctedFile(payload)
      const isMultiple = payload.length > 1

      const fileName = isMultiple
        ? `files_${Date.now()}.zip`
        : mockEntities.find(item => selectedItems.includes(item.uuid))
            ?.fileName || 'downloaded_file'

      saveBlobAsFile(blob, fileName)

      setSelectedItems([])
    } catch (error) {
      const safeMessage = sanitizeText(error?.message || 'Download failed')

      showSnackbar(safeMessage || 'Download failed', 'error')
      console.error('Error downloading file(s):', sanitizeForLog(error))
    } finally {
      setStartDownloadingSingleFile(false)
    }
  }

  const handleDownloadErrorFile = async () => {
    if (selectedItems.length === 0) return

    // 1️⃣ Block items that are not Failed or Partially Uploaded
    const invalidItems = mockEntities.filter(
      item =>
        selectedItems.includes(item.uuid) &&
        item?.fileStatus !== 'Failed' &&
        item?.fileStatus !== 'Partially Uploaded'
    )

    if (invalidItems.length > 0) {
      showSnackbar(
        'Only FAILED or PARTIALLY UPLOADED items can download error files',
        'error'
      )
      return
    }

    // 2️⃣ Block Vendor Transactional Data type
    const vendorTxnItems = mockEntities.filter(
      item =>
        selectedItems.includes(item.uuid) &&
        item?.dataType === 'Vendor Transactional Data'
    )

    if (vendorTxnItems.length > 0) {
      showSnackbar(
        'Error file cannot be downloaded for Vendor Transactional Data Type',
        'error'
      )
      return
    }

    try {
      setStartDownloadingSingleFile(true)

      const payload = mockEntities
        .filter(item => selectedItems.includes(item.uuid))
        .map(item => item.loadId)

      const result = await downloadSelctedErrorFile(payload)

      if (result?.success && result?.data) {
        showSnackbar(
          `Request Initiated for Download With Id: ${result.data}`,
          'success'
        )
      } else if (result?.error) {
        showSnackbar(result.error, 'error')
      }

      setSelectedItems([])
    } catch (error) {
      const safeMessage = sanitizeText(error?.message || 'Download failed')
      showSnackbar(safeMessage, 'error')
    } finally {
      setStartDownloadingSingleFile(false)
    }
  }

  return (
    <>
      <FileUploadUI
        fetchMetaData={fetchMetaData}
        onSourcesLoaded={setSources}
        onDataTypesLoaded={setDataTypes}
      />

      {/* Subheader */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          Status of Uploaded Data
        </Typography>

        {/* Right side - Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Individual action buttons */}

          {selectedItems?.length > 0 && (
            <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_ORIGINAL_FILE}>
              <IconButton
                sx={{
                  color: 'var(--forbrand-primary)',
                }}
                onClick={handleSingleDownload}
                disabled={
                  startDownloadingSingleFile || selectedItems.length === 0
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.374 2.5L14.4102 2.50684C14.4212 2.5115 14.431 2.51879 14.4395 2.52734L14.4424 2.53027L19.5928 7.70996V7.71094C19.6029 7.72125 19.6108 7.73395 19.6162 7.74707L19.625 7.78906V21.3975C19.6249 21.4246 19.6138 21.4505 19.5947 21.4697C19.5756 21.4889 19.5496 21.4999 19.5225 21.5H4.47754C4.45044 21.4999 4.42443 21.4889 4.40527 21.4697C4.38616 21.4505 4.37506 21.4246 4.375 21.3975V2.60254C4.37506 2.57545 4.38616 2.54948 4.40527 2.53027C4.41492 2.52063 4.42611 2.51293 4.43848 2.50781L4.47754 2.5H14.374ZM4.57227 21.2949H19.4277V7.88867H14.2754V2.70508H4.57227V21.2949ZM12.0557 11.1865V18.0127L14.5908 15.4775L14.6055 15.4609C14.6118 15.4534 14.6191 15.4468 14.6279 15.4424C14.6367 15.438 14.6465 15.4351 14.6562 15.4346C14.6661 15.4341 14.6763 15.436 14.6855 15.4395C14.6948 15.443 14.7038 15.4482 14.7109 15.4551C14.718 15.462 14.7238 15.4704 14.7275 15.4795C14.7313 15.4887 14.7327 15.4989 14.7324 15.5088C14.7322 15.5186 14.7297 15.5282 14.7256 15.5371C14.7214 15.5461 14.7155 15.5549 14.708 15.5615L14.6963 15.5723L14.6846 15.584L11.999 18.2822L9.30371 15.5801L9.30273 15.5791L9.28711 15.5557C9.28346 15.5469 9.28125 15.5369 9.28125 15.5273C9.28131 15.518 9.28352 15.5086 9.28711 15.5C9.29058 15.4917 9.29549 15.484 9.30176 15.4775C9.3162 15.4637 9.33545 15.4561 9.35547 15.4561C9.36561 15.4561 9.37555 15.4582 9.38477 15.4619L9.41016 15.4785L11.9277 17.9961V11.1846L12.0557 11.1865ZM14.4727 7.68359H19.2832L18.4346 6.83105L15.3271 3.70605L14.4727 2.84668V7.68359Z"
                    fill="var(--forbrand-primary)"
                    stroke="var(--forbrand-primary)"
                  />
                </svg>
              </IconButton>
            </Tooltip>
          )}

          {selectedItems?.length > 0 && (
            <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_ERROR_FILE}>
              <IconButton
                sx={{
                  color: 'var(--forbrand-primary)',
                }}
                onClick={handleDownloadErrorFile}
                disabled={
                  startDownloadingSingleFile || selectedItems.length === 0
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.374 2.5L14.4102 2.50684C14.4212 2.5115 14.431 2.51879 14.4395 2.52734L14.4424 2.53027L19.5928 7.70996V7.71094C19.6029 7.72125 19.6108 7.73395 19.6162 7.74707L19.625 7.78906V21.3975C19.6249 21.4246 19.6138 21.4505 19.5947 21.4697C19.5756 21.4889 19.5496 21.4999 19.5225 21.5H4.47754C4.45044 21.4999 4.42443 21.4889 4.40527 21.4697C4.38616 21.4505 4.37506 21.4246 4.375 21.3975V2.60254C4.37506 2.57545 4.38616 2.54948 4.40527 2.53027C4.41492 2.52063 4.42611 2.51293 4.43848 2.50781L4.47754 2.5H14.374ZM4.57227 21.2949H19.4277V7.88867H14.2754V2.70508H4.57227V21.2949ZM11.9805 17.6143H11.9824C12.0112 17.6141 12.0398 17.619 12.0664 17.6299C12.0931 17.6409 12.1182 17.6573 12.1387 17.6777C12.159 17.698 12.1745 17.7225 12.1855 17.749C12.1966 17.7756 12.2031 17.8042 12.2031 17.833V17.835C12.2033 17.8789 12.1903 17.9223 12.166 17.959C12.1418 17.9956 12.1069 18.0241 12.0664 18.041C12.0258 18.058 11.9807 18.0622 11.9375 18.0537C11.8945 18.0452 11.8543 18.0241 11.8232 17.9932C11.7923 17.9622 11.7713 17.9228 11.7627 17.8799C11.7541 17.8368 11.7586 17.7916 11.7754 17.751C11.7922 17.7105 11.821 17.6758 11.8574 17.6514C11.8939 17.6271 11.9366 17.6143 11.9805 17.6143ZM12.1084 11.2168V15.2607H11.8584V11.2168H12.1084ZM14.4727 7.68359H19.2832L18.4346 6.83105L15.3271 3.70605L14.4727 2.84668V7.68359Z"
                    fill="var(--forbrand-primary)"
                    stroke="var(--forbrand-primary)"
                  />
                </svg>
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <IconButton onClick={handleDownload} disabled={startDownloading}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton onClick={handleSearch}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.DELETE}>
            <IconButton
              sx={{
                color: 'var(--forbrand-error)',
              }}
              onClick={handleBulkDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTable
          columns={columns}
          data={mockEntities}
          loading={loading}
          emptyMessage="No entities found"
          showCheckbox={true}
          selectedItems={selectedItems}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          showActions={false}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showFooter={true}
        />
      </Box>

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
      <ConfirmationDialog
        open={deleteDialogOpen}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete selected record?"
        message="This action can’t be undone. Doing so may impact other functionalities in the system."
        confirmText="Proceed"
        itemType=""
        type="delete"
        showCancelButton={true}
        onCancel={handleCancel}
      />
    </>
  )
}

export default ReturnUpload
